'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { Button, FormField, Input, LinkButton, Text } from '@/components'
import { apiClient, ClientRoutes } from '@/config'

import {
  ForgotPasswordFormFields,
  ForgotPasswordFormSchemaType,
  forgotPasswordFormSchema,
} from './ForgotPasswordForm.schema'
import { MessageSeverity, useAppStore } from '@/store'
import { cn } from '@/lib/utils'
import { useLocalizedRoutes } from '@/lib/hooks'

export const ForgotPasswordForm: React.FC = () => {
  const form = useForm<ForgotPasswordFormSchemaType>({
    mode: 'onTouched',
    resolver: yupResolver(forgotPasswordFormSchema),
  })

  const { control, handleSubmit, getValues, reset } = form
  const {
    status: { loading },
    setLoading,
    setMessage,
  } = useAppStore()
  const t = useTranslations('auth.forgotPasswordForm')
  const { get: _ } = useLocalizedRoutes()

  const onSubmitValid: SubmitHandler<ForgotPasswordFormSchemaType> =
    useCallback(async () => {
      const formValues = getValues()
      setLoading(true)
      try {
        const { data } = await apiClient.postForgotPassword({
          email: formValues.email,
        })
        if (data?.success) {
          setMessage({
            message: t('emailSent'),
            severity: MessageSeverity.SUCCESS,
          })
          reset({
            email: '',
          })
        } else {
          setMessage({
            message: t('error.couldNotSendForgotEmail'),
            severity: MessageSeverity.SUCCESS,
          })
        }
      } catch (error) {
        setMessage({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message: t((error as any)?.message ?? 'error.unknownError'),
          severity: MessageSeverity.ERROR,
        })
      }
      setLoading(false)
    }, [getValues, reset, setLoading, setMessage, t])

  const onClickSubmit = useCallback(() => {
    handleSubmit(onSubmitValid, () => {
      setMessage({
        message: t('error.invalidEmail'),
        severity: MessageSeverity.ERROR,
      })
    })()
  }, [handleSubmit, onSubmitValid, setMessage, t])

  return (
    <div
      className={cn(
        'w-full h-full flex-1',
        'bg-waves relative',
        'w-full h-full bg-no-repeat bg-cover bg-local pt-6 bg-position-[top_300px_center]',
      )}
    >
      <div className="xl:max-w-2xl py-8 px-12 border border-white/15 rounded-xl text-white mx-auto mt-24 mb-24">
        <FormProvider {...form}>
          <Text variant="heading2Xl" className="my-3">
            {t('title')}
          </Text>
          <Text variant="subtitle" className="my-4">
            {t('subheader')}
          </Text>

          <div className="text-center space-y-4">
            <div className="space-y-2">
              <FormField
                control={control}
                name={ForgotPasswordFormFields.email}
                disabled={loading}
                render={({ field }) => (
                  <Input
                    type="email"
                    inputMode="email"
                    {...field}
                    placeholder={t('fields.email')}
                    className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary border-white/40"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <Button
              disabled={loading}
              onClick={() => onClickSubmit()}
              className="w-auto p-6 text-lg my-6 rounded-full text-primary"
              variant="glow"
              size="lg"
            >
              {t('buttonCaption')}
            </Button>
          </div>
          <div className="mt-4 text-sm text-center text-gray-100">
            <p className="mb-5 text-lg text-white my-3">
              {t('dontHaveAccount')}
            </p>
            <LinkButton
              variant="ghost"
              className="rounded-full text-lg"
              size="lg"
              href={_(ClientRoutes.signUp)}
            >
              {t('signUp')}
            </LinkButton>
          </div>
        </FormProvider>
      </div>
    </div>
  )
}
